/**
 * CR082: Canonical Source Type Definitions
 * Single source of truth for all source types used in merchant_sources
 * 
 * DB stores ONLY canonical values (lowercase snake_case)
 * UI shows human-readable labels
 */

// Canonical source types (lowercase snake_case) - this is what gets stored in DB
export const CANONICAL_SOURCE_TYPES = [
  "official_giftcard",
  "gift_card_page", 
  "official_flyer",
  "ontario_flyer_search",
  "offers_page",
  "deals_promos",
  "official_home",
  "redemption_portal",
  "fallback",
  "fallback_distributor",
  "html",
  "forum",
  "other",
] as const;

export type CanonicalSourceType = typeof CANONICAL_SOURCE_TYPES[number];

// Human-readable labels for UI display
export const SOURCE_TYPE_LABELS: Record<CanonicalSourceType, string> = {
  official_giftcard: "Official Gift Card",
  gift_card_page: "Gift Card Page",
  official_flyer: "Official Flyer",
  ontario_flyer_search: "Ontario Flyer Search",
  offers_page: "Offers Page",
  deals_promos: "Deals & Promos",
  official_home: "Official Home",
  redemption_portal: "Redemption Portal",
  fallback: "Fallback",
  fallback_distributor: "Fallback Distributor",
  html: "HTML Page",
  forum: "Forum",
  other: "Other",
};

// Pipeline usage descriptions - explains what each source type is used for
export const SOURCE_TYPE_USAGE: Record<CanonicalSourceType, string> = {
  official_giftcard: "Used for gift card deal scanning on merchant sites",
  gift_card_page: "Alternative gift card page for scanning",
  official_flyer: "Used for flyer ingestion pipeline (PDF/image flyers)",
  ontario_flyer_search: "Flipp/aggregator search for Ontario flyers",
  offers_page: "General offers/promotions page scanning",
  deals_promos: "Dedicated deals & promotions section",
  official_home: "Merchant homepage for general deal discovery",
  redemption_portal: "Gift card redemption/balance check portals",
  fallback: "Fallback source when primary sources fail",
  fallback_distributor: "Third-party distributor as fallback",
  html: "Partner/affiliate HTML page sources",
  forum: "Community forum deal sources",
  other: "Miscellaneous source type",
};

// Mapping from legacy/mixed-case values to canonical values
export const SOURCE_TYPE_ALIASES: Record<string, CanonicalSourceType> = {
  // Uppercase variants
  "OFFICIAL_GIFTCARD": "official_giftcard",
  "GIFT_CARD_PAGE": "gift_card_page",
  "OFFICIAL_FLYER": "official_flyer",
  "ONTARIO_FLYER_SEARCH": "ontario_flyer_search",
  "OFFERS_PAGE": "offers_page",
  "DEALS_PROMOS": "deals_promos",
  "OFFICIAL_HOME": "official_home",
  "REDEMPTION_PORTAL": "redemption_portal",
  "FALLBACK": "fallback",
  "FALLBACK_DISTRIBUTOR": "fallback_distributor",
  "HTML": "html",
  "FORUM": "forum",
  "OTHER": "other",
  // Mixed/legacy variants
  "flyer": "official_flyer",
  "giftcard": "official_giftcard",
  "giftcard_page": "gift_card_page",
  "LOYALTY_PAGE": "other",
  "BLOG_NEWS": "other",
};

// ============================================================================
// BUCKET SYSTEM - Groups source types for UI organization
// ============================================================================

export const SOURCE_TYPE_BUCKETS = [
  "official_gift_card",
  "official_flyer", 
  "official_home",
  "forums",
  "affiliated_partners",
  "other",
] as const;

export type SourceTypeBucket = typeof SOURCE_TYPE_BUCKETS[number];

export const BUCKET_LABELS: Record<SourceTypeBucket, string> = {
  official_gift_card: "Official Gift Card",
  official_flyer: "Official Flyer",
  official_home: "Official Home",
  forums: "Forums",
  affiliated_partners: "Affiliated Partners",
  other: "Other",
};

export const BUCKET_DESCRIPTIONS: Record<SourceTypeBucket, string> = {
  official_gift_card: "Gift card pages on merchant sites for deal scanning",
  official_flyer: "Flyer sources including PDF/image flyers and aggregator searches",
  official_home: "Merchant homepages for general deal discovery",
  forums: "Community forum deal sources",
  affiliated_partners: "Partner/affiliate-driven sources (HTML pages, embedded partners, distributors)",
  other: "Miscellaneous and uncategorized sources",
};

/**
 * Deterministic bucket assignment for a source type
 */
export function bucketForSourceType(sourceType: CanonicalSourceType): SourceTypeBucket {
  switch (sourceType) {
    // Official Gift Card bucket
    case "official_giftcard":
    case "gift_card_page":
    case "redemption_portal":
      return "official_gift_card";
    
    // Official Flyer bucket (includes ontario_flyer_search)
    case "official_flyer":
    case "ontario_flyer_search":
      return "official_flyer";
    
    // Official Home bucket
    case "official_home":
    case "offers_page":
    case "deals_promos":
      return "official_home";
    
    // Forums bucket
    case "forum":
      return "forums";
    
    // Affiliated Partners bucket
    case "html":
    case "fallback":
    case "fallback_distributor":
      return "affiliated_partners";
    
    // Other bucket (catch-all)
    case "other":
    default:
      return "other";
  }
}

/**
 * Get all source types that belong to a bucket
 */
export function getSourceTypesForBucket(bucket: SourceTypeBucket): CanonicalSourceType[] {
  return CANONICAL_SOURCE_TYPES.filter(type => bucketForSourceType(type) === bucket);
}

/**
 * Normalize any source type string to its canonical form
 * Handles: uppercase, legacy aliases, and unknown values
 */
export function normalizeSourceType(value: string | null | undefined): CanonicalSourceType {
  if (!value) return "other";
  
  const trimmed = value.trim();
  
  // Check if it's already canonical
  if (CANONICAL_SOURCE_TYPES.includes(trimmed as CanonicalSourceType)) {
    return trimmed as CanonicalSourceType;
  }
  
  // Check aliases (handles uppercase and legacy values)
  if (SOURCE_TYPE_ALIASES[trimmed]) {
    return SOURCE_TYPE_ALIASES[trimmed];
  }
  
  // Try lowercase conversion
  const lower = trimmed.toLowerCase();
  if (CANONICAL_SOURCE_TYPES.includes(lower as CanonicalSourceType)) {
    return lower as CanonicalSourceType;
  }
  
  // Unknown value - default to "other"
  console.warn(`Unknown source_type "${value}" normalized to "other"`);
  return "other";
}

/**
 * Get human-readable label for a source type
 */
export function getSourceTypeLabel(value: string | null | undefined): string {
  const canonical = normalizeSourceType(value);
  return SOURCE_TYPE_LABELS[canonical] || canonical;
}

/**
 * Get pipeline usage description for a source type
 */
export function getSourceTypeUsage(value: string | null | undefined): string {
  const canonical = normalizeSourceType(value);
  return SOURCE_TYPE_USAGE[canonical] || "Unknown usage";
}

// ============================================================================
// DROPDOWN OPTIONS - Only the 5 approved bucket-level options for the UI
// ============================================================================

// These are the ONLY options shown in the Edit Source dropdown
// Maps to bucket-level canonical values
export const DROPDOWN_SOURCE_TYPES = [
  "official_giftcard",
  "official_flyer",
  "official_home",
  "forum",
  "html", // Represents "Affiliated Partners" bucket
] as const;

export type DropdownSourceType = typeof DROPDOWN_SOURCE_TYPES[number];

// Labels for dropdown display
export const DROPDOWN_LABELS: Record<DropdownSourceType, string> = {
  official_giftcard: "Official Gift Card",
  official_flyer: "Official Flyer",
  official_home: "Official Home",
  forum: "Forums",
  html: "Affiliated Partners",
};

/**
 * Get dropdown options for UI (only 5 approved options)
 */
export function getSourceTypeOptions(): Array<{ value: DropdownSourceType; label: string; usage: string }> {
  return DROPDOWN_SOURCE_TYPES.map((value) => ({
    value,
    label: DROPDOWN_LABELS[value],
    usage: SOURCE_TYPE_USAGE[value],
  }));
}
